/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Unsubscribe } from "firebase/auth";
import { query, limit, orderBy, collection, onSnapshot, getFirestore } from "firebase/firestore";
import firebase from "app/Auth";
import { AppDispatch, RootState } from "app/store";
import { ChatRoomMessage } from "features/message/types";
import { messageActions, selectAllParentChatRooms } from "features/message/slice";

export const useParentChatSubscribe = () => {
  const dispatch = useDispatch<AppDispatch>();
  const chatRooms = useSelector(selectAllParentChatRooms);
  const parent = useSelector((state: RootState) => state.user.parent);

  // 購読中のルームIDとunsubscribe関数を保持
  const subscribesRef = useRef<Map<string, Unsubscribe>>(new Map());

  const subscribeParentChatRoomMessage = (chatRoomId: string, uid: string): Unsubscribe => {
    const now = dayjs().format("YYYY-MM-DDTHH:mm:ssZ");
    dispatch(
      messageActions.updateChatRoomMessageLastSyncAt({
        chatRoomId: chatRoomId,
        uid,
        lastMessageSyncAt: now,
      }),
    );

    const db = getFirestore(firebase);
    const initialMessages: ChatRoomMessage[] = [];
    // 常に20件フェッチ
    const messages = query(
      collection(db, "rooms", chatRoomId, "messages"),
      orderBy("created_at", "desc"),
      limit(20),
    );

    let initial = true;
    const unsubscribe = onSnapshot(
      messages,
      (snapshot) => {
        if (initial) {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              initialMessages.push(change.doc.data() as ChatRoomMessage);
              if (change.newIndex === snapshot.size - 1) {
                dispatch(
                  messageActions.bulkInsertChatRoomMessage({
                    uid,
                    messages: initialMessages,
                  }),
                );
              }
            }
          });
          initial = false;
        } else {
          snapshot.docChanges().forEach((change) => {
            switch (change.type) {
              case "added":
                dispatch(
                  messageActions.insertChatRoomMessage({
                    uid,
                    message: change.doc.data() as ChatRoomMessage,
                  }),
                );
                break;
            }
          });
        }
      },
      (error) => {
        console.error(error);
      },
    );

    return unsubscribe;
  };

  useEffect(() => {
    if (!parent?.uid) return;

    const currentRoomIds = new Set(chatRooms.map((room) => room.id));
    const subscribedRoomIds = new Set(subscribesRef.current.keys());

    // 新規追加されたルームのみ購読開始
    chatRooms.forEach((chatRoom) => {
      if (!subscribedRoomIds.has(chatRoom.id)) {
        const unsubscribe = subscribeParentChatRoomMessage(chatRoom.id, parent.uid);
        subscribesRef.current.set(chatRoom.id, unsubscribe);
      }
    });

    // 削除されたルームのみ購読解除
    subscribedRoomIds.forEach((roomId) => {
      if (!currentRoomIds.has(roomId)) {
        const unsubscribe = subscribesRef.current.get(roomId);
        if (unsubscribe) {
          unsubscribe();
          subscribesRef.current.delete(roomId);
        }
      }
    });
  }, [chatRooms, parent?.uid]);

  // コンポーネントアンマウント時に全購読解除
  useEffect(() => {
    return () => {
      subscribesRef.current.forEach((unsubscribe) => unsubscribe());
      subscribesRef.current.clear();
    };
  }, []);
};
